import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { GatewayAPIError } from "../../app/api/client";
import { IconClose, IconRefresh } from "../chats/chatIcons";
import { applyDocumentTheme, normalizeThemePreference, writeCachedTheme, type ThemePreference } from "../theme/theme";
import { normalizeSupportedLocale, patchSelfProfile, type SelfProfile, type SupportedLocale } from "./profileApi";
import { ProfileSettingsPanel } from "./ProfileSettingsPanel";
import "./profileAvatar.css";

export interface AvatarPreviewSelection {
  file: Blob;
  filename: string;
  previewURL: string;
}

interface ProfileAvatarPanelProps {
  open: boolean;
  loading: boolean;
  profile: SelfProfile | null;
  errorMessage?: string | null;
  onClose: () => void;
  onReload: () => Promise<void>;
  onReplaceAvatar: (avatar: Blob, filename: string) => Promise<void>;
  onRemoveAvatar: () => Promise<void>;
  patchThemePreference?: (input: { theme: ThemePreference }) => Promise<Pick<SelfProfile, "theme">>;
  applyThemePreference?: (theme: ThemePreference) => void;
  persistThemePreference?: (theme: ThemePreference) => void;
  onProfileUpdated?: (profile: SelfProfile) => void;
  onThemeApplied?: (theme: ThemePreference) => void;
}

type FeedbackTone = "error" | "success";
type ActionMode = "reload" | "remove" | "replace";

interface FeedbackState {
  tone: FeedbackTone;
  text: string;
}

interface ResolveProfileSettingsBindingsInput {
  profile: SelfProfile | null;
  disabled: boolean;
  patchThemePreference?: (input: { theme: ThemePreference }) => Promise<Pick<SelfProfile, "theme">>;
  applyThemePreference?: (theme: ThemePreference) => void;
  persistThemePreference?: (theme: ThemePreference) => void;
  onProfileUpdated?: (profile: SelfProfile) => void;
  onThemeApplied?: (theme: ThemePreference) => void;
}

interface ProfileSettingsBindings {
  displayName: string;
  nickname: string;
  locale: SupportedLocale;
  theme: ThemePreference;
  disabled: boolean;
  patchProfile: (input: {
    display_name?: string;
    nickname?: string;
    locale?: SupportedLocale;
    theme?: ThemePreference;
  }) => Promise<Partial<Pick<SelfProfile, "display_name" | "nickname" | "locale" | "theme">>>;
  applyTheme: (theme: ThemePreference) => void;
  persistTheme: (theme: ThemePreference) => void;
  onThemeApplied?: (theme: ThemePreference) => void;
}

export const PROFILE_AVATAR_PANEL_CLASSES = {
  root: "profile-avatar-panel",
  actions: "profile-avatar-actions",
} as const;

export function resolveProfileSettingsBindings(input: ResolveProfileSettingsBindingsInput): ProfileSettingsBindings {
  const patchThemePreference = input.patchThemePreference;

  return {
    displayName: input.profile?.display_name.trim() ?? "",
    nickname: input.profile?.nickname.trim() ?? "",
    locale: normalizeSupportedLocale(input.profile?.locale),
    theme: normalizeThemePreference(input.profile?.theme),
    disabled: input.disabled,
    patchProfile: async (patchInput) => {
      if (patchInput.theme !== undefined && patchInput.locale === undefined && patchThemePreference !== undefined) {
        return patchThemePreference({ theme: patchInput.theme });
      }
      const updated = await patchSelfProfile(patchInput);
      input.onProfileUpdated?.(updated);
      return updated;
    },
    applyTheme: input.applyThemePreference ?? applyDocumentTheme,
    persistTheme: input.persistThemePreference ?? writeCachedTheme,
    onThemeApplied: input.onThemeApplied,
  };
}

function toActionError(error: unknown, fallback: string, t: (key: string) => string): string {
  if (!(error instanceof GatewayAPIError)) {
    return error instanceof Error ? error.message : fallback;
  }

  switch (error.code) {
    case "invalid_request":
      return t("profileAvatar.errors.invalidRequest");
    case "forbidden":
      return t("profileAvatar.errors.forbidden");
    case "unauthorized":
      return t("profileAvatar.errors.unauthorized");
    default:
      return fallback;
  }
}

export function createAvatarPreviewSelection(
  file: Blob,
  filename: string,
  createObjectURL: (input: Blob) => string = (input) => URL.createObjectURL(input),
): AvatarPreviewSelection {
  const normalizedFilename = filename.trim();
  if (normalizedFilename === "") {
    throw new Error("avatar filename must not be empty");
  }

  return {
    file,
    filename: normalizedFilename,
    previewURL: createObjectURL(file),
  };
}

export function resolveAvatarImageSource(
  profile: SelfProfile | null,
  pending: AvatarPreviewSelection | null,
): string {
  if (pending !== null) {
    return pending.previewURL;
  }
  return profile?.avatar_url ?? "";
}

export function ProfileAvatarPanel({
  open,
  loading,
  profile,
  errorMessage = null,
  onClose,
  onReload,
  onReplaceAvatar,
  onRemoveAvatar,
  patchThemePreference,
  applyThemePreference,
  persistThemePreference,
  onProfileUpdated,
  onThemeApplied,
}: ProfileAvatarPanelProps) {
  const { t } = useTranslation();
  const [pendingSelection, setPendingSelection] = useState<AvatarPreviewSelection | null>(null);
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [actionMode, setActionMode] = useState<ActionMode | null>(null);

  useEffect(() => {
    return () => {
      if (pendingSelection !== null) {
        URL.revokeObjectURL(pendingSelection.previewURL);
      }
    };
  }, [pendingSelection]);

  useEffect(() => {
    if (errorMessage === null || errorMessage.trim() === "") {
      return;
    }
    setFeedback({
      tone: "error",
      text: errorMessage,
    });
  }, [errorMessage]);

  const avatarSource = resolveAvatarImageSource(profile, pendingSelection);
  const displayName = profile?.display_name.trim() || profile?.nickname.trim() || t("profileAvatar.youFallback");
  const disabled = loading || actionMode !== null;
  const replaceDisabled = disabled || pendingSelection === null;
  const settingsBindings = resolveProfileSettingsBindings({
    profile,
    disabled,
    patchThemePreference,
    applyThemePreference,
    persistThemePreference,
    onProfileUpdated,
    onThemeApplied,
  });

  const onFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile === undefined) {
      return;
    }

    setPendingSelection((previous) => {
      if (previous !== null) {
        URL.revokeObjectURL(previous.previewURL);
      }
      return createAvatarPreviewSelection(selectedFile, selectedFile.name);
    });
    setFeedback(null);
    event.target.value = "";
  };

  const onReloadClick = async () => {
    setActionMode("reload");
    setFeedback(null);
    try {
      await onReload();
    } catch (error) {
      setFeedback({
        tone: "error",
        text: toActionError(error, t("profileAvatar.errors.reloadFailed"), t),
      });
    } finally {
      setActionMode(null);
    }
  };

  const onReplaceClick = async () => {
    if (pendingSelection === null) {
      return;
    }

    setActionMode("replace");
    setFeedback(null);
    try {
      await onReplaceAvatar(pendingSelection.file, pendingSelection.filename);
      URL.revokeObjectURL(pendingSelection.previewURL);
      setPendingSelection(null);
      setFeedback({
        tone: "success",
        text: t("profileAvatar.feedback.updated"),
      });
    } catch (error) {
      setFeedback({
        tone: "error",
        text: toActionError(error, t("profileAvatar.errors.updateFailed"), t),
      });
    } finally {
      setActionMode(null);
    }
  };

  const onRemoveClick = async () => {
    setActionMode("remove");
    setFeedback(null);
    try {
      await onRemoveAvatar();
      setPendingSelection((current) => {
        if (current !== null) {
          URL.revokeObjectURL(current.previewURL);
        }
        return null;
      });
      setFeedback({
        tone: "success",
        text: t("profileAvatar.feedback.removed"),
      });
    } catch (error) {
      setFeedback({
        tone: "error",
        text: toActionError(error, t("profileAvatar.errors.removeFailed"), t),
      });
    } finally {
      setActionMode(null);
    }
  };

  return (
    <section
      className={`${PROFILE_AVATAR_PANEL_CLASSES.root}${open ? " profile-avatar-panel-open" : ""}`}
      data-testid="profile-avatar-panel"
      aria-hidden={!open}
    >
      <header className="profile-avatar-head">
        <div className="chat-shell-heading">
          <p className="chat-shell-eyebrow">{t("profileAvatar.eyebrow")}</p>
          <h2 className="chat-section-title">{t("profileAvatar.title")}</h2>
        </div>
        <div className="chat-head-actions">
          <button
            className="icon-button icon-button-ghost"
            type="button"
            onClick={() => void onReloadClick()}
            aria-label={t("profileAvatar.actions.reload")}
            title={t("profileAvatar.actions.reload")}
            disabled={disabled}
          >
            <IconRefresh />
          </button>
          <button
            className="icon-button icon-button-ghost"
            type="button"
            onClick={onClose}
            aria-label={t("profileAvatar.actions.closePanel")}
            title={t("profileAvatar.actions.closePanel")}
          >
            <IconClose />
          </button>
        </div>
      </header>

      <div className="profile-avatar-content">
        <div className="profile-avatar-preview" aria-label={t("profileAvatar.previewAria", { displayName })}>
          {avatarSource === "" ? (
            <span className="profile-avatar-placeholder">{displayName.slice(0, 1).toUpperCase()}</span>
          ) : (
            <img src={avatarSource} alt="" />
          )}
        </div>
        <p className="muted-line">
          {pendingSelection === null
            ? t("profileAvatar.hints.chooseForPreview")
            : t("profileAvatar.hints.selected", { fileName: pendingSelection.filename })}
        </p>
      </div>

      <input
        className="sr-only"
        id="profile-avatar-file-input"
        data-testid="profile-avatar-file-input"
        type="file"
        accept="image/*"
        onChange={onFileSelect}
      />

      <div className={PROFILE_AVATAR_PANEL_CLASSES.actions}>
        <label className="secondary-button profile-avatar-upload-label" htmlFor="profile-avatar-file-input">
          {t("profileAvatar.actions.chooseImage")}
        </label>
        <button
          className="primary-button"
          type="button"
          onClick={() => void onReplaceClick()}
          disabled={replaceDisabled}
        >
          {t("profileAvatar.actions.replacePhoto")}
        </button>
        <button className="danger-button" type="button" onClick={() => void onRemoveClick()} disabled={disabled}>
          {t("profileAvatar.actions.removePhoto")}
        </button>
      </div>

      <ProfileSettingsPanel
        displayName={settingsBindings.displayName}
        nickname={settingsBindings.nickname}
        locale={settingsBindings.locale}
        theme={settingsBindings.theme}
        disabled={settingsBindings.disabled}
        patchProfile={settingsBindings.patchProfile}
        applyTheme={settingsBindings.applyTheme}
        persistTheme={settingsBindings.persistTheme}
        onThemeApplied={settingsBindings.onThemeApplied}
      />

      {feedback !== null ? (
        <p className={`status-pill profile-avatar-feedback profile-avatar-feedback-${feedback.tone}`}>{feedback.text}</p>
      ) : null}
    </section>
  );
}
